import {gql} from '@apollo/client';

const BillingProfileForPlan = gql`
    fragment BillingProfileForPlan on BillingProfile {
        isDeleted
        billingProfileId
        title
        attendanceSchedule {
            id
            fullYear
            title
        }
        billingScheme {
            id
            title
        }
        fundingTypes {
            id
            title
            ... on FundingPlan {
                periods {
                    from
                    to
                    rates {
                        rate
                    }
                }
            }
        }
        annualization {
            invoices
            weeksOfCare
        }
    }
`;

export const GetBillingProfilesForPlan = gql`
    query GetBillingProfilesForPlan($institutionId: InstitutionSetId!, $billingProfileIds: [BillingProfileId!]) {
        finance {
            billingProfiles {
                list(institutionSetId: $institutionId) {
                    ...BillingProfileForPlan
                }
            }
            oldBillingProfiles: billingProfiles {
                list(billingProfileId: $billingProfileIds) {
                    ...BillingProfileForPlan
                }
            }
        }
    }
    ${BillingProfileForPlan}
`;

export const GetPlansPreview = gql`
    query PreviewPlans($planInputs: [PlanInput!]!, $siteId: SiteId!) {
        finance {
            plans {
                preview(planInputs: $planInputs, siteId: $siteId) {
                    childId
                    note
                    planId
                    behaviors {
                        id
                        payload
                    }
                    planParts {
                        planPartId
                        order
                        attendanceScheduleId
                        termTimeOnly
                        billingProfileId
                        billingScheme
                        weeksOfCare
                        invoices
                        publicFundingSettings {
                            ... on PlanManualPublicFunding {
                                amount
                                fundingTypeIds
                                maxMinutesPerDay
                                period
                                time {
                                    ... on PlanFundingTimeMinutes {
                                        minutes
                                    }
                                    ... on PlanFundingTimeHours {
                                        hours
                                    }
                                }
                            }
                            ... on PlanManualByRatePublicFunding {
                                fundingTypeIds
                                maxMinutesPerDay
                                period
                                rate
                                time {
                                    ... on PlanFundingTimeMinutes {
                                        minutes
                                    }
                                    ... on PlanFundingTimeHours {
                                        hours
                                    }
                                }
                            }
                            ... on PlanAssistedPublicFunding {
                                fundingTypeIds
                                maxMinutesPerDay
                                period
                                time {
                                    ... on PlanFundingTimeMinutes {
                                        minutes
                                    }
                                    ... on PlanFundingTimeHours {
                                        hours
                                    }
                                }
                            }
                        }
                        priceModifiers {
                            ... on PlanDiscount {
                                amount
                                percent
                                period
                                showOnInvoice
                                title
                                ordering
                            }
                            ... on PlanPresetDiscount {
                                discountPreset {
                                    accountId
                                    active
                                    amount
                                    discountPresetId
                                    ordering
                                    siteId
                                    title
                                    ordering
                                }
                            }
                            ... on PlanSurcharge {
                                amount
                                percent
                                period
                                showOnInvoice
                                title
                                ordering
                            }
                        }
                        sessionBookings {
                            allowFunding
                            prices {
                                price
                                pricingGroupId
                            }
                            planRuleId
                            range {
                                from
                                to
                            }
                            sessionAvailability {
                                day
                                session {
                                    active
                                    billOnChildAbsent
                                    billOnChildSick
                                    billOnClosingDays
                                    color
                                    customFte
                                    discountable
                                    flexibleHours
                                    fundable
                                    institutionSetId
                                    pricing {
                                        adHocPrice
                                        period
                                        price
                                        pricingGroupId
                                        volume
                                    }
                                    sessionAvailabilities {
                                        day
                                        session {
                                            active
                                            billOnChildAbsent
                                            billOnChildSick
                                            billOnClosingDays
                                            color
                                            customFte
                                            discountable
                                            flexibleHours
                                            fundable
                                            institutionSetId
                                            pricing {
                                                adHocPrice
                                                period
                                                price
                                                pricingGroupId
                                                volume
                                            }
                                            sessionAvailabilities {
                                                day
                                                sessionAvailabilityId
                                                timeRange {
                                                    from
                                                    to
                                                }
                                            }
                                            sessionId
                                            sessionVersionId
                                            title
                                            type
                                            validFrom
                                            validTo
                                            volumeDiscountGroupId
                                        }
                                        sessionAvailabilityId
                                        timeRange {
                                            from
                                            to
                                        }
                                    }
                                    sessionId
                                    sessionVersionId
                                    title
                                    type
                                    validFrom
                                    validTo
                                    volumeDiscountGroupId
                                }
                                sessionAvailabilityId
                                timeRange {
                                    from
                                    to
                                }
                            }
                            sessionBookingId
                        }
                        productBookings {
                            amount
                            prices {
                                price
                                pricingGroupId
                            }
                            bookedPrice
                            planRuleId
                            bookingStatus
                            isVirtual
                            productAvailability {
                                day
                                product {
                                    active
                                    billOnChildAbsent
                                    billOnChildSick
                                    billOnClosingDays
                                    color
                                    discountable
                                    institutionSetId
                                    pricing {
                                        adHocPrice
                                        period
                                        price
                                        pricingGroupId
                                        volume
                                    }
                                    productAvailabilities {
                                        day
                                        productAvailabilityId
                                        product {
                                            active
                                            billOnChildAbsent
                                            billOnChildSick
                                            billOnClosingDays
                                            color
                                            discountable
                                            institutionSetId
                                            pricing {
                                                adHocPrice
                                                period
                                                price
                                                pricingGroupId
                                                volume
                                            }
                                            productAvailabilities {
                                                day
                                                productAvailabilityId
                                            }
                                            productId
                                            productVersionId
                                            title
                                            validFrom
                                            validTo
                                        }
                                    }
                                    productId
                                    productVersionId
                                    title
                                    validFrom
                                    validTo
                                }
                                productAvailabilityId
                            }
                        }
                    }
                    planStates {
                        appliedFunding {
                            amount
                            fundedBookings {
                                amount
                                minutes
                                planPartId
                                sessionBookingId
                            }
                            period
                            time {
                                ... on PlanFundingTimeMinutes {
                                    minutes
                                }
                                ... on PlanFundingTimeHours {
                                    hours
                                }
                            }
                        }
                        planPartState {
                            monthlyEstimate
                            planPartId
                            pricingGroupId
                            weeklyTotal
                        }
                        range {
                            from
                            to
                        }
                    }
                    pricing {
                        ... on PricingGroupPricing {
                            pricingGroupId
                        }
                        ... on AutomaticPricing {
                            ruleGroupId
                        }
                    }
                    range {
                        from
                        to
                    }
                }
            }
        }
    }
`;

export const CreatePlan = gql`
    mutation CreatePlan($planInputs: [PlanInput!]!, $siteId: SiteId!) {
        finance {
            plans {
                create(planInputs: $planInputs, siteId: $siteId) {
                    childId
                    note
                    planId
                    behaviors {
                        id
                        payload
                    }
                    planParts {
                        planPartId
                        order
                        attendanceScheduleId
                        termTimeOnly
                        billingProfileId
                        billingScheme
                        weeksOfCare
                        invoices
                        publicFundingSettings {
                            ... on PlanManualPublicFunding {
                                amount
                                fundingTypeIds
                                maxMinutesPerDay
                                period
                                time {
                                    ... on PlanFundingTimeMinutes {
                                        minutes
                                    }
                                    ... on PlanFundingTimeHours {
                                        hours
                                    }
                                }
                            }
                            ... on PlanManualByRatePublicFunding {
                                fundingTypeIds
                                maxMinutesPerDay
                                period
                                rate
                                time {
                                    ... on PlanFundingTimeMinutes {
                                        minutes
                                    }
                                    ... on PlanFundingTimeHours {
                                        hours
                                    }
                                }
                            }
                            ... on PlanAssistedPublicFunding {
                                fundingTypeIds
                                maxMinutesPerDay
                                period
                                time {
                                    ... on PlanFundingTimeMinutes {
                                        minutes
                                    }
                                    ... on PlanFundingTimeHours {
                                        hours
                                    }
                                }
                            }
                        }
                        priceModifiers {
                            ... on PlanDiscount {
                                amount
                                percent
                                period
                                showOnInvoice
                                title
                                ordering
                            }
                            ... on PlanPresetDiscount {
                                discountPreset {
                                    accountId
                                    active
                                    amount
                                    discountPresetId
                                    ordering
                                    siteId
                                    title
                                    ordering
                                }
                            }
                            ... on PlanSurcharge {
                                amount
                                percent
                                period
                                showOnInvoice
                                title
                                ordering
                            }
                        }
                        sessionBookings {
                            allowFunding
                            prices {
                                price
                                pricingGroupId
                            }
                            planRuleId
                            range {
                                from
                                to
                            }
                            sessionAvailability {
                                day
                                session {
                                    active
                                    billOnChildAbsent
                                    billOnChildSick
                                    billOnClosingDays
                                    color
                                    customFte
                                    discountable
                                    flexibleHours
                                    fundable
                                    institutionSetId
                                    pricing {
                                        adHocPrice
                                        period
                                        price
                                        pricingGroupId
                                        volume
                                    }
                                    sessionAvailabilities {
                                        day
                                        session {
                                            active
                                            billOnChildAbsent
                                            billOnChildSick
                                            billOnClosingDays
                                            color
                                            customFte
                                            discountable
                                            flexibleHours
                                            fundable
                                            institutionSetId
                                            pricing {
                                                adHocPrice
                                                period
                                                price
                                                pricingGroupId
                                                volume
                                            }
                                            sessionAvailabilities {
                                                day
                                                sessionAvailabilityId
                                                timeRange {
                                                    from
                                                    to
                                                }
                                            }
                                            sessionId
                                            sessionVersionId
                                            title
                                            type
                                            validFrom
                                            validTo
                                            volumeDiscountGroupId
                                        }
                                        sessionAvailabilityId
                                        timeRange {
                                            from
                                            to
                                        }
                                    }
                                    sessionId
                                    sessionVersionId
                                    title
                                    type
                                    validFrom
                                    validTo
                                    volumeDiscountGroupId
                                }
                                sessionAvailabilityId
                                timeRange {
                                    from
                                    to
                                }
                            }
                            sessionBookingId
                        }
                        productBookings {
                            amount
                            prices {
                                price
                                pricingGroupId
                            }
                            bookedPrice
                            planRuleId
                            bookingStatus
                            productAvailability {
                                day
                                product {
                                    active
                                    billOnChildAbsent
                                    billOnChildSick
                                    billOnClosingDays
                                    color
                                    discountable
                                    institutionSetId
                                    pricing {
                                        adHocPrice
                                        period
                                        price
                                        pricingGroupId
                                        volume
                                    }
                                    productAvailabilities {
                                        day
                                        productAvailabilityId
                                        product {
                                            active
                                            billOnChildAbsent
                                            billOnChildSick
                                            billOnClosingDays
                                            color
                                            discountable
                                            institutionSetId
                                            pricing {
                                                adHocPrice
                                                period
                                                price
                                                pricingGroupId
                                                volume
                                            }
                                            productAvailabilities {
                                                day
                                                productAvailabilityId
                                            }
                                            productId
                                            productVersionId
                                            title
                                            validFrom
                                            validTo
                                        }
                                    }
                                    productId
                                    productVersionId
                                    title
                                    validFrom
                                    validTo
                                }
                                productAvailabilityId
                            }
                        }
                    }
                    planStates {
                        appliedFunding {
                            amount
                            fundedBookings {
                                amount
                                minutes
                                planPartId
                                sessionBookingId
                            }
                            period
                            time {
                                ... on PlanFundingTimeMinutes {
                                    minutes
                                }
                                ... on PlanFundingTimeHours {
                                    hours
                                }
                            }
                        }
                        planPartState {
                            monthlyEstimate
                            planPartId
                            pricingGroupId
                            weeklyTotal
                        }
                        range {
                            from
                            to
                        }
                    }
                    pricing {
                        ... on PricingGroupPricing {
                            pricingGroupId
                        }
                        ... on AutomaticPricing {
                            ruleGroupId
                        }
                    }
                    range {
                        from
                        to
                    }
                }
            }
        }
    }
`;
